import { ClearFiltersIcon, SyncDesktopIcon } from '@/components/icons'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { TestPlanStatusBadge } from '@/components/projects/TestPlanStatusBadge'
import { CreateTestPlanDialog } from '@/components/projects/testing/CreateTestPlanDialog'
import { CreateTestSuitesDialog } from '@/components/projects/testing/CreateTestSuitesDialog'
import { DeleteTestPlanDialog } from '@/components/projects/testing/DeleteTestPlanDialog'
import { DeleteTestSuiteDialog } from '@/components/projects/testing/DeleteTestSuiteDialog'
import { PushTestItemToDevOpsDialog } from '@/components/projects/testing/PushTestItemToDevOpsDialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { MultiSelect } from '@/components/ui/multi-select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuth, useConnectors, useDebounce, usePagination, useProject, useTestPlans } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import { getInitials } from '@/lib/utils'
import type { TestPlan, TestPlanFilters, TestSuite } from '@/types'
import { TEST_PLAN_DEFAULT_SORT_DIRECTIONS, testPlanStatusFilterOptions, type TestPlanSortColumn } from '@/types/testPlan'
import { ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown, Plus, Search, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

const SORT_KEYS = ['sort', 'sortBy'] as const

export function TestPlansPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const { hasPermission } = useAuth(projectId)
  const { data: project } = useProject(projectId)

  const [search, setSearch] = useState('')
  const [statuses, setStatuses] = useState<string[]>([])
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [addSuitesPlan, setAddSuitesPlan] = useState<TestPlan | null>(null)
  const [deletePlan, setDeletePlan] = useState<TestPlan | null>(null)
  const [deleteSuite, setDeleteSuite] = useState<TestSuite | null>(null)
  const [pushTarget, setPushTarget] = useState<{ mode: 'plan' | 'suite'; id: string; title: string; identifier: string; subtitle?: string; statusBadge?: ReactNode } | null>(null)

  const { page, pageSize, paginationParams, setPage, setPageSize, resetPage } = usePagination()
  const debouncedSearch = useDebounce(search, 300)

  const [searchParams, setSearchParams] = useSearchParams()
  const sortStorageKey = `test-plans-sort-${projectId}`

  const hasUrlSort = SORT_KEYS.some(k => searchParams.has(k))
  const sessionSort = useMemo(
    () => new URLSearchParams(hasUrlSort ? '' : (sessionStorage.getItem(sortStorageKey) ?? '')),
    [hasUrlSort, sortStorageKey],
  )

  const sortByParam = searchParams.get('sortBy') ?? sessionSort.get('sortBy')
  const sortBy: TestPlanSortColumn =
    sortByParam && sortByParam in TEST_PLAN_DEFAULT_SORT_DIRECTIONS ? (sortByParam as TestPlanSortColumn) : 'number'
  const sortDirParam = searchParams.get('sort') ?? sessionSort.get('sort')
  const sortDirection: 'asc' | 'desc' =
    sortDirParam === 'asc' || sortDirParam === 'desc' ? sortDirParam : TEST_PLAN_DEFAULT_SORT_DIRECTIONS[sortBy]

  const persistSort = useCallback((params: URLSearchParams) => {
    const sortParams = new URLSearchParams()
    SORT_KEYS.forEach(k => {
      const v = params.get(k)
      if (v) sortParams.set(k, v)
    })
    sessionStorage.setItem(sortStorageKey, sortParams.toString())
  }, [sortStorageKey])

  const didRestoreSortRef = useRef(false)
  useEffect(() => {
    if (didRestoreSortRef.current) return
    didRestoreSortRef.current = true
    if (SORT_KEYS.some(k => searchParams.has(k))) return
    const saved = sessionStorage.getItem(sortStorageKey)
    if (!saved) return
    const savedParams = new URLSearchParams(saved)
    if (!Array.from(savedParams.keys()).length) return
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      savedParams.forEach((value, key) => next.set(key, value))
      return next
    }, { replace: true })
  }, [searchParams, setSearchParams, sortStorageKey])

  const handleSortChange = (column: TestPlanSortColumn) => {
    const nextDirection =
      column === sortBy ? (sortDirection === 'asc' ? 'desc' : 'asc') : TEST_PLAN_DEFAULT_SORT_DIRECTIONS[column]
    const next = new URLSearchParams(searchParams)
    next.set('sortBy', column)
    next.set('sort', nextDirection)
    setSearchParams(next, { replace: true })
    persistSort(next)
    resetPage()
  }

  const filters = useMemo<TestPlanFilters>(() => {
    const f: TestPlanFilters = { sortBy, sortDirection }
    if (debouncedSearch) f.search = debouncedSearch
    if (statuses.length > 0) f.statuses = statuses
    return f
  }, [debouncedSearch, statuses, sortBy, sortDirection])

  useEffect(() => {
    resetPage()
  }, [debouncedSearch, statuses, resetPage])

  const { data, isLoading } = useTestPlans(projectId!, paginationParams, filters)
  const testPlans = data?.items ?? []
  const totalCount = data?.totalCount ?? 0

  const canCreate = hasPermission(Permissions.Project.TestPlan.Create, projectId)
  const canCreateTestSuite = hasPermission(Permissions.Project.TestSuite.Create, projectId)
  const canDeleteTestPlan = hasPermission(Permissions.Project.TestPlan.Delete, projectId)
  const canDeleteTestSuite = hasPermission(Permissions.Project.TestSuite.Delete, projectId)
  const canPushTestPlan = hasPermission(Permissions.Project.TestPlan.Push, projectId)
  const canPushTestSuite = hasPermission(Permissions.Project.TestSuite.Push, projectId)

  const { data: connectors = [] } = useConnectors(projectId!)
  const azureDevOpsConnected = useMemo(() => connectors.some(c => c.type === 'AzureDevOps' && c.isEnabled), [connectors])

  const toggleRow = useCallback((id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const anyExpanded = expandedRows.size > 0

  const toggleAll = useCallback(() => {
    if (anyExpanded) {
      setExpandedRows(new Set())
    } else {
      setExpandedRows(new Set(testPlans.map(tp => tp.id)))
    }
  }, [anyExpanded, testPlans])

  const handleTestSuiteClick = (testSuite: TestSuite) => {
    navigate(
      `/projects/${projectId}/testing-integration/test-suites/${testSuite.id}`,
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-foreground font-medium">Project: {project?.name ?? 'Loading...'}</p>
          <h1 className="text-3xl font-bold">Testing & Integration</h1>
        </div>
        {canCreate && (
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="new-test-plan-button">
            <Plus className="mr-2 h-4 w-4" />
            New Test Plan
          </Button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="The list is searchable by #, test plan title, user"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-9"
            aria-label="Search test plans"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <MultiSelect
          options={testPlanStatusFilterOptions}
          value={statuses}
          onChange={setStatuses}
          placeholder="All Statuses"
          displayMode="compact"
          searchable={false}
          showCompactIcon={false}
          showCompactOverflowCount
          className="w-[200px]"
          aria-label="Filter by status"
        />
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setSearch('')
            setStatuses([])
            setSearchParams(prev => {
              const next = new URLSearchParams(prev)
              SORT_KEYS.forEach(k => next.delete(k))
              persistSort(next)
              return next
            }, { replace: true })
            resetPage()
          }}
          disabled={!search && statuses.length === 0 && sortBy === 'number' && sortDirection === TEST_PLAN_DEFAULT_SORT_DIRECTIONS.number}
          className="flex items-center gap-1.5 text-sm font-medium"
          aria-label="Clear all filters"
        >
          <ClearFiltersIcon className="h-4 w-4" />
          Clear All
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3" role="status" aria-live="polite" aria-label="Loading test plans">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table aria-label="Test plans table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[56px]">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={toggleAll}
                    aria-label={anyExpanded ? 'Collapse all rows' : 'Expand all rows'}
                  >
                    {anyExpanded ? <ChevronsDownUp className="h-4 w-4" /> : <ChevronsUpDown className="h-4 w-4" />}
                  </Button>
                </TableHead>
                <SortableTableHead
                  column="number"
                  label="#"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={TEST_PLAN_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={handleSortChange}
                  className="w-[100px]"
                />
                <SortableTableHead
                  column="title"
                  label="Title"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={TEST_PLAN_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={handleSortChange}
                />
                <SortableTableHead
                  column="status"
                  label="Status"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={TEST_PLAN_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={handleSortChange}
                  className="w-[120px]"
                />
                <SortableTableHead
                  column="user"
                  label="User"
                  sortBy={sortBy}
                  sortDirection={sortDirection}
                  defaultDirections={TEST_PLAN_DEFAULT_SORT_DIRECTIONS}
                  onSortChange={handleSortChange}
                  className="w-[180px]"
                />
                <TableHead className="w-[270px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {totalCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground" data-testid="test-plans-empty">
                    No test plans match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                testPlans.map((tp) => (
                  <TestPlanRow
                    key={tp.id}
                    testPlan={tp}
                    isExpanded={expandedRows.has(tp.id)}
                    onToggle={() => toggleRow(tp.id)}
                    onTestSuiteClick={handleTestSuiteClick}
                    canAddTestSuite={canCreateTestSuite}
                    onAddTestSuite={() => setAddSuitesPlan(tp)}
                    canDeleteTestPlan={canDeleteTestPlan}
                    onDeleteTestPlan={() => setDeletePlan(tp)}
                    canDeleteTestSuite={canDeleteTestSuite}
                    onDeleteTestSuite={(ts) => setDeleteSuite(ts)}
                    canPushTestPlan={canPushTestPlan}
                    onPushTestPlan={() => setPushTarget({
                      mode: 'plan',
                      id: tp.id,
                      title: tp.title,
                      identifier: tp.testPlanIdentifier,
                      subtitle: `Test suites: ${tp.testSuites.length} • Test Cases: ${tp.testSuites.reduce((sum, s) => sum + s.testCaseCount, 0)}`,
                      statusBadge: <TestPlanStatusBadge status={tp.status} />,
                    })}
                    canPushTestSuite={canPushTestSuite}
                    onPushTestSuite={(ts) => setPushTarget({
                      mode: 'suite',
                      id: ts.id,
                      title: ts.title,
                      identifier: ts.testSuiteIdentifier,
                      subtitle: `Test Cases: ${ts.testCaseCount}`,
                    })}
                    azureDevOpsConnected={azureDevOpsConnected}
                  />
                ))
              )}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}

      <CreateTestPlanDialog
        projectId={projectId!}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {addSuitesPlan && (
        <CreateTestSuitesDialog
          projectId={projectId!}
          testPlanId={addSuitesPlan.id}
          testPlanIdentifier={addSuitesPlan.testPlanIdentifier}
          testPlanTitle={addSuitesPlan.title}
          open={!!addSuitesPlan}
          onOpenChange={(open) => {
            if (!open) setAddSuitesPlan(null)
          }}
        />
      )}

      {deletePlan && (
        <DeleteTestPlanDialog
          projectId={projectId!}
          testPlan={deletePlan}
          open={!!deletePlan}
          onOpenChange={(open) => {
            if (!open) setDeletePlan(null)
          }}
        />
      )}

      {deleteSuite && (
        <DeleteTestSuiteDialog
          projectId={projectId!}
          testSuite={deleteSuite}
          open={!!deleteSuite}
          onOpenChange={(open) => {
            if (!open) setDeleteSuite(null)
          }}
        />
      )}

      {pushTarget && (
        <PushTestItemToDevOpsDialog
          projectId={projectId!}
          mode={pushTarget.mode}
          itemId={pushTarget.id}
          itemTitle={pushTarget.title}
          itemIdentifier={pushTarget.identifier}
          itemSubtitle={pushTarget.subtitle}
          statusBadge={pushTarget.statusBadge}
          open={!!pushTarget}
          onOpenChange={(open) => {
            if (!open) setPushTarget(null)
          }}
        />
      )}
    </div>
  )
}

interface TestPlanRowProps {
  testPlan: TestPlan
  isExpanded: boolean
  onToggle: () => void
  onTestSuiteClick: (testSuite: TestSuite) => void
  canAddTestSuite: boolean
  onAddTestSuite: () => void
  canDeleteTestPlan: boolean
  onDeleteTestPlan: () => void
  canDeleteTestSuite: boolean
  onDeleteTestSuite: (ts: TestSuite) => void
  canPushTestPlan: boolean
  onPushTestPlan: () => void
  canPushTestSuite: boolean
  onPushTestSuite: (ts: TestSuite) => void
  azureDevOpsConnected: boolean
}

const PUSH_DISABLED_TOOLTIP = 'Connect Azure DevOps to enable push'

function TestPlanRow({ testPlan, isExpanded, onToggle, onTestSuiteClick, canAddTestSuite, onAddTestSuite, canDeleteTestPlan, onDeleteTestPlan, canDeleteTestSuite, onDeleteTestSuite, canPushTestPlan, onPushTestPlan, canPushTestSuite, onPushTestSuite, azureDevOpsConnected }: TestPlanRowProps) {
  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
        data-testid={`test-plan-row-${testPlan.testPlanIdentifier}`}
      >
        <TableCell>
          {isExpanded ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </TableCell>
        <TableCell className="font-medium text-foreground">{testPlan.testPlanIdentifier}</TableCell>
        <TableCell className="font-medium">
          {testPlan.title}
        </TableCell>
        <TableCell><TestPlanStatusBadge status={testPlan.status} /></TableCell>
        <TableCell>
          {testPlan.createdByUserName && (
            <div className="flex items-center gap-2">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                  {getInitials(testPlan.createdByUserName)}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 text-sm" data-testid="test-plan-created-by">{testPlan.createdByUserName}</span>
            </div>
          )}
        </TableCell>
        <TableCell onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            {canAddTestSuite && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onAddTestSuite}
                className="px-2 text-foreground hover:text-foreground"
                data-testid={`add-test-suite-button-${testPlan.testPlanIdentifier}`}
              >
                <Plus className="mr-1 h-4 w-4 text-action-icon" />
                New Test Suite
              </Button>
            )}
            {canPushTestPlan && (
              <span
                className={azureDevOpsConnected ? 'inline-flex' : 'inline-flex cursor-not-allowed'}
                title={azureDevOpsConnected ? undefined : PUSH_DISABLED_TOOLTIP}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onPushTestPlan}
                  disabled={!azureDevOpsConnected}
                  className="px-2 text-card-foreground hover:text-card-foreground"
                  aria-label={`Push test plan ${testPlan.testPlanIdentifier} to Azure DevOps`}
                  data-testid={`push-test-plan-button-${testPlan.testPlanIdentifier}`}
                >
                  <SyncDesktopIcon className="mr-1 h-4 w-4 text-action-icon" />
                  Push
                </Button>
              </span>
            )}
            {canDeleteTestPlan && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onDeleteTestPlan}
                className="px-2"
                aria-label={`Delete test plan ${testPlan.testPlanIdentifier}`}
                data-testid={`delete-test-plan-button-${testPlan.testPlanIdentifier}`}
              >
                <Trash2 className="h-4 w-4 text-action-icon" />
              </Button>
          )}
          </div>
        </TableCell>
      </TableRow>
      {isExpanded && testPlan.testSuites.length > 0 && (
        testPlan.testSuites.map((ts) => (
          <TableRow
            key={ts.id}
            className="cursor-pointer hover:bg-muted/30 transition-colors bg-muted/10"
            onClick={() => onTestSuiteClick(ts)}
            data-testid={`test-suite-row-${ts.testSuiteIdentifier}`}
          >
            <TableCell />
            <TableCell className="text-sm text-muted-foreground font-medium">
              #{ts.testSuiteIdentifier}
            </TableCell>
            <TableCell colSpan={3} className="text-sm">
              {ts.title}: <span className="font-bold">{ts.testCaseCount} Test Cases</span>
            </TableCell>
            <TableCell onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-end gap-1">
                {canPushTestSuite && (
                  <span
                    className={azureDevOpsConnected ? 'inline-flex' : 'inline-flex cursor-not-allowed'}
                    title={azureDevOpsConnected ? undefined : PUSH_DISABLED_TOOLTIP}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onPushTestSuite(ts)}
                      disabled={!azureDevOpsConnected}
                      className="px-2 text-foreground hover:text-foreground"
                      aria-label={`Push test suite ${ts.testSuiteIdentifier} to Azure DevOps`}
                      data-testid={`push-test-suite-button-${ts.testSuiteIdentifier}`}
                    >
                      <SyncDesktopIcon className="mr-1 h-4 w-4 text-action-icon" />
                      Push
                    </Button>
                  </span>
                )}
                {canDeleteTestSuite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeleteTestSuite(ts)}
                    className="px-2"
                    aria-label={`Delete test suite ${ts.testSuiteIdentifier}`}
                    data-testid={`delete-test-suite-button-${ts.testSuiteIdentifier}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))
      )}
    </>
  )
}
